import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Tabs, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Checkbox, Alert, Button,
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import { providersAPI, pricingAPI, powerTypesAPI, regionsAPI } from '../../../services/api';
import { Provider, Region, PowerType, PowerVMPricingRow } from '../../../types';

interface PriceChangeMap {
  [pricingId: string]: { costPerQuarterCore?: number; costPerGBRam?: number; isAvailable?: boolean };
}

const PowerVMPricing = () => {
  const [providers, setProviders] = useState<Provider[]>([]);
  const [selectedProvider, setSelectedProvider] = useState(0);
  const [powerTypes, setPowerTypes] = useState<PowerType[]>([]);
  const [regions, setRegions] = useState<Region[]>([]);
  const [pricing, setPricing] = useState<PowerVMPricingRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [changes, setChanges] = useState<PriceChangeMap>({});

  const fetchInitialData = async () => {
    try {
      const [providersRes, powerTypesRes] = await Promise.all([providersAPI.getAll(), powerTypesAPI.getAll()]);
      // Only show IBM providers
      const ibmProviders = providersRes.data.filter((p: Provider) => p.IsActive && p.ProviderName.toLowerCase().includes('ibm'));
      setProviders(ibmProviders);
      setPowerTypes(powerTypesRes.data.filter((pt: PowerType) => pt.IsActive));
    } catch { setError('Failed to load data'); } finally { setLoading(false); }
  };

  const fetchPricingData = useCallback(async () => {
    if (providers.length === 0) return;
    try {
      const currentProvider = providers[selectedProvider];
      if (!currentProvider) return;
      const [pricingRes, regionsRes] = await Promise.all([
        pricingAPI.getPowerVMPricing(currentProvider.ProviderId),
        regionsAPI.getAll(currentProvider.ProviderId),
      ]);
      setRegions(regionsRes.data.filter((r: Region) => r.IsActive));
      setPricing(pricingRes.data);
      setChanges({});
    } catch { setError('Failed to load pricing'); }
  }, [providers, selectedProvider]);

  useEffect(() => { fetchInitialData(); }, []);
  useEffect(() => { if (providers.length > 0) fetchPricingData(); }, [selectedProvider, providers, fetchPricingData]);

  const handlePriceChange = (pricingId: number, field: string, value: number | boolean) => {
    setChanges({ ...changes, [pricingId]: { ...changes[pricingId], [field]: value } });
  };

  const handleSaveChanges = async () => {
    try {
      const updates = Object.entries(changes).map(([pricingId, changedFields]) => {
        const original = pricing.find(p => p.PowerVMPricingId === parseInt(pricingId));
        return pricingAPI.updatePowerVMPrice(pricingId, {
          costPerQuarterCore: changedFields.costPerQuarterCore !== undefined ? changedFields.costPerQuarterCore : original?.CostPerQuarterCore ?? 0,
          costPerGBRam: changedFields.costPerGBRam !== undefined ? changedFields.costPerGBRam : original?.CostPerGBRam ?? 0,
          isAvailable: changedFields.isAvailable !== undefined ? changedFields.isAvailable : original?.IsAvailable ?? 0,
        });
      });
      await Promise.all(updates);
      setSuccess('Prices updated successfully');
      setChanges({});
      fetchPricingData();
      setTimeout(() => setSuccess(''), 3000);
    } catch { setError('Failed to update prices'); }
  };

  const getFieldValue = (pricingId: number, field: 'costPerQuarterCore' | 'costPerGBRam', defaultValue: number) =>
    changes[pricingId]?.[field] !== undefined ? changes[pricingId]![field]! : defaultValue;

  const getAvailableValue = (pricingId: number, defaultValue: number) =>
    changes[pricingId]?.isAvailable !== undefined ? changes[pricingId]!.isAvailable! : !!defaultValue;

  if (loading) return <Typography>Loading...</Typography>;
  if (providers.length === 0) return <Box><Typography variant="h4" sx={{ mb: 2 }}>Power VM Pricing</Typography><Alert severity="info">No IBM providers configured. Power VM pricing is only available for IBM Cloud providers.</Alert></Box>;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Power VM Pricing</Typography>
        <Button variant="contained" color="secondary" startIcon={<SaveIcon />} onClick={handleSaveChanges} disabled={Object.keys(changes).length === 0}>
          Save Changes ({Object.keys(changes).length})
        </Button>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
      {providers.length > 1 && (
        <Paper sx={{ mb: 2 }}>
          <Tabs value={selectedProvider} onChange={(_e, v: number) => setSelectedProvider(v)} variant="fullWidth">
            {providers.map((p) => <Tab key={p.ProviderId} label={p.ProviderName} />)}
          </Tabs>
        </Paper>
      )}
      {powerTypes.map((pt) => {
        const entries = pricing.filter(p => p.PowerTypeId === pt.PowerTypeId);
        return (
          <Paper key={pt.PowerTypeId} sx={{ mb: 3, p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              {pt.PowerTypeName}
              {pt.Description && <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 0.5, fontWeight: 400 }}>&ndash; {pt.Description}</Typography>}
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Field</TableCell>
                    {regions.map((r) => <TableCell key={r.RegionId} align="center">{r.RegionName}</TableCell>)}
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>Cost / 0.25 Core ($)</TableCell>
                    {regions.map((r) => {
                      const entry = entries.find(e => e.RegionId === r.RegionId);
                      if (!entry) return <TableCell key={r.RegionId} />;
                      return (
                        <TableCell key={r.RegionId} align="center">
                          <TextField type="number" size="small" value={getFieldValue(entry.PowerVMPricingId, 'costPerQuarterCore', entry.CostPerQuarterCore)}
                            onChange={(e) => handlePriceChange(entry.PowerVMPricingId, 'costPerQuarterCore', e.target.value === '' ? 0 : parseFloat(e.target.value))}
                            inputProps={{ step: 0.01, min: 0 }} sx={{ width: 100, '& .MuiInputBase-input': { fontSize: '0.875rem', py: 0.5, px: 1 } }} />
                        </TableCell>
                      );
                    })}
                  </TableRow>
                  <TableRow>
                    <TableCell>Cost / GB RAM ($)</TableCell>
                    {regions.map((r) => {
                      const entry = entries.find(e => e.RegionId === r.RegionId);
                      if (!entry) return <TableCell key={r.RegionId} />;
                      return (
                        <TableCell key={r.RegionId} align="center">
                          <TextField type="number" size="small" value={getFieldValue(entry.PowerVMPricingId, 'costPerGBRam', entry.CostPerGBRam)}
                            onChange={(e) => handlePriceChange(entry.PowerVMPricingId, 'costPerGBRam', e.target.value === '' ? 0 : parseFloat(e.target.value))}
                            inputProps={{ step: 0.01, min: 0 }} sx={{ width: 100, '& .MuiInputBase-input': { fontSize: '0.875rem', py: 0.5, px: 1 } }} />
                        </TableCell>
                      );
                    })}
                  </TableRow>
                  <TableRow>
                    <TableCell>Enabled</TableCell>
                    {regions.map((r) => {
                      const entry = entries.find(e => e.RegionId === r.RegionId);
                      if (!entry) return <TableCell key={r.RegionId} />;
                      return (
                        <TableCell key={r.RegionId} align="center">
                          <Checkbox checked={getAvailableValue(entry.PowerVMPricingId, entry.IsAvailable) as boolean}
                            onChange={(e) => handlePriceChange(entry.PowerVMPricingId, 'isAvailable', e.target.checked)} />
                        </TableCell>
                      );
                    })}
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        );
      })}
    </Box>
  );
};

export default PowerVMPricing;
